import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Layers, CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { Button } from '@brix/ui'
import { supabase } from '@/infrastructure/supabase/client'
import { useAuthStore } from '@/store/authStore'
import type { UserRole, Plan, CustomPlanLimits } from '@brix/types'

interface InviteInfo {
  id: string
  email: string
  role: string
  tenant_name: string
  expires_at: string
}

export function InvitePage() {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const { user, setTenant } = useAuthStore()

  const [invite, setInvite] = useState<InviteInfo | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'accepting' | 'done' | 'error' | 'expired'>('loading')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    if (!token) { setStatus('expired'); return }
    supabase.rpc('get_invite_by_token', { p_token: token }).then(({ data }) => {
      if (!data) { setStatus('expired'); return }
      setInvite(data as unknown as InviteInfo)
      setStatus('ready')
    })
  }, [token])

  const accept = async () => {
    if (!user) {
      // Store token in sessionStorage then redirect to login
      sessionStorage.setItem('pendingInvite', token)
      void navigate(`/login?invite=${token}`)
      return
    }
    setStatus('accepting')
    const { data } = await supabase.rpc('accept_invite', { p_token: token })
    const result = data as { ok?: boolean; error?: string; role?: string } | null
    if (result?.error) {
      setErrorMsg(result.error)
      setStatus('error')
      return
    }
    // Reload tenant membership
    const { data: userData } = await supabase
      .from('users')
      .select('role, tenants(id, name, slug, plan, custom_limits, created_at, updated_at)')
      .eq('id', user.id)
      .single()
    if (userData?.tenants) {
      const t = userData.tenants
      setTenant(
        {
          id: t.id,
          name: t.name,
          slug: t.slug,
          plan: t.plan as Plan,
          customLimits: t.custom_limits as unknown as CustomPlanLimits | null,
          createdAt: t.created_at,
          updatedAt: t.updated_at,
        },
        userData.role as UserRole,
      )
    }
    setStatus('done')
  }

  const ROLE_LABELS: Record<string, string> = {
    owner: 'Owner', admin: 'Admin', editor: 'Editor', author: 'Author', member: 'Member',
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md text-center">
        <div className="mb-6 flex justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-600">
            <Layers className="h-7 w-7 text-white" />
          </div>
        </div>

        {status === 'loading' && (
          <div className="flex items-center justify-center gap-2 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Verifying invite…</span>
          </div>
        )}

        {status === 'expired' && (
          <div className="rounded-2xl bg-white p-8 shadow-sm border border-gray-100">
            <XCircle className="mx-auto mb-4 h-12 w-12 text-red-400" />
            <h1 className="text-xl font-bold text-gray-900">Invite not found</h1>
            <p className="mt-2 text-sm text-gray-500">
              This invite link is invalid or has expired. Ask the workspace admin to send a new invite.
            </p>
            <Link to="/login" className="mt-6 block">
              <Button className="w-full">Go to login</Button>
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="rounded-2xl bg-white p-8 shadow-sm border border-gray-100">
            <XCircle className="mx-auto mb-4 h-12 w-12 text-red-400" />
            <h1 className="text-xl font-bold text-gray-900">Could not accept invite</h1>
            <p className="mt-2 text-sm text-red-600">{errorMsg}</p>
            {!user && (
              <p className="mt-3 text-sm text-gray-500">
                Make sure you're logged in with the email this invite was sent to.
              </p>
            )}
            <Link to="/" className="mt-6 block">
              <Button variant="secondary" className="w-full">Go to dashboard</Button>
            </Link>
          </div>
        )}

        {(status === 'ready' || status === 'accepting') && invite && (
          <div className="rounded-2xl bg-white p-8 shadow-sm border border-gray-100">
            <h1 className="text-2xl font-bold text-gray-900">You're invited!</h1>
            <p className="mt-3 text-gray-500">
              You've been invited to join <strong className="text-gray-900">{invite.tenant_name}</strong>{' '}
              as <span className="rounded-full bg-violet-100 px-2 py-0.5 text-sm font-semibold text-violet-700">
                {ROLE_LABELS[invite.role] ?? invite.role}
              </span>
            </p>
            <p className="mt-1 text-xs text-gray-400">
              Invite sent to {invite.email} · Expires {new Date(invite.expires_at).toLocaleDateString()}
            </p>

            {user && user.email !== invite.email && (
              <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
                You're signed in as <strong>{user.email}</strong>, but this invite is for <strong>{invite.email}</strong>.
                Sign in with the correct account to accept.
              </div>
            )}

            <div className="mt-6 space-y-3">
              <Button
                onClick={() => void accept()}
                loading={status === 'accepting'}
                className="w-full"
                disabled={!!(user && user.email !== invite.email)}
              >
                {user ? 'Accept invitation' : 'Sign in to accept'}
              </Button>
              {!user && (
                <Link to={`/signup?invite=${token}`}>
                  <Button variant="secondary" className="w-full">
                    Create account & accept
                  </Button>
                </Link>
              )}
            </div>
          </div>
        )}

        {status === 'done' && (
          <div className="rounded-2xl bg-white p-8 shadow-sm border border-gray-100">
            <CheckCircle className="mx-auto mb-4 h-12 w-12 text-green-500" />
            <h1 className="text-xl font-bold text-gray-900">Welcome aboard!</h1>
            <p className="mt-2 text-sm text-gray-500">
              You've joined <strong>{invite?.tenant_name}</strong>. You're all set.
            </p>
            <button
              onClick={() => void navigate('/', { replace: true })}
              className="mt-6 w-full"
            >
              <Button className="w-full">Go to dashboard</Button>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
